import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { customerApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Logo } from '../../components/Logo';
import { 
    Building, Globe, Target, CheckCircle, Clock, ArrowRight, ArrowLeft 
} from 'lucide-react';

const steps = [
    { title: 'Company Info', icon: Building },
    { title: 'Solutions', icon: Target },
    { title: 'Targeting', icon: Globe },
];

export default function OnboardingPage() {
    const { user, updateUser, logout } = useAuth();
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [form, setForm] = useState({
        company_name: user?.company_name || '',
        website: '',
        company_summary: '',
        business_objectives: '',
        solutions: '',
        value_propositions: '',
        target_industries: '',
        target_company_sizes: '',
        target_geography: '',
        icp_summary: '',
    });

    useEffect(() => {
        checkExistingDetails();
    }, []);

    const checkExistingDetails = async () => {
        try {
            const response = await customerApi.getDetails();
            if (response.data) {
                if (!response.data.is_approved) {
                    setIsPending(true);
                }
            }
        } catch (error) {
            // No details yet
        }
    };

    const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSubmit = async () => {
        if (!form.company_name.trim()) {
            toast.error('Company name is required');
            return;
        }
        setSaving(true);
        try {
            await customerApi.saveDetails({
                company_name: form.company_name,
                website: form.website,
                company_summary: form.company_summary,
                business_objectives: form.business_objectives,
                solutions: form.solutions.split('\n').filter(s => s.trim()),
                value_propositions: form.value_propositions.split('\n').filter(s => s.trim()),
                target_industries: form.target_industries.split(',').map(s => s.trim()).filter(Boolean),
                target_company_sizes: form.target_company_sizes.split(',').map(s => s.trim()).filter(Boolean),
                target_geography: form.target_geography.split(',').map(s => s.trim()).filter(Boolean),
                icp_summary: form.icp_summary,
            });
            updateUser({ ...user, is_onboarded: true });
            setIsPending(true);
            toast.success('Details submitted! Awaiting admin approval.');
        } catch (error) {
            toast.error('Failed to submit details');
        } finally {
            setSaving(false);
        }
    };

    if (isPending) {
        return (
            <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
                <div className="card-base p-10 max-w-lg text-center" data-testid="onboarding-pending">
                    <div className="w-20 h-20 rounded-full bg-channel-ads/20 flex items-center justify-center mx-auto mb-6">
                        <Clock className="w-10 h-10 text-channel-ads" />
                    </div>
                    <h2 className="text-2xl font-heading font-bold text-white mb-3">
                        Awaiting Approval
                    </h2>
                    <p className="text-content-secondary mb-6">
                        Your company details have been submitted successfully. Your NovantPro admin team will review and approve your account shortly.
                    </p>
                    <p className="text-sm text-content-muted mb-6">
                        Once approved, your dashboard and reports will become accessible.
                    </p>
                    <Button variant="ghost" className="btn-ghost" onClick={logout} data-testid="onboarding-logout-btn">
                        Sign Out
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app flex items-center justify-center p-4" data-testid="onboarding-page">
            <div className="card-base p-8 max-w-2xl w-full">
                <div className="flex items-center justify-center mb-8">
                    <Logo />
                </div>
                <h2 className="text-2xl font-heading font-bold text-white text-center mb-2">
                    Welcome to NovantPro
                </h2>
                <p className="text-content-secondary text-center mb-8">
                    Let's set up your account. Fill in your company details to get started.
                </p>

                {/* Step indicators */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {steps.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                                i <= step ? 'bg-brand-red text-white' : 'bg-surface-app text-content-muted border border-line-default'
                            }`}>
                                {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                            </div>
                            <span className={`text-xs hidden sm:inline ${i <= step ? 'text-white' : 'text-content-muted'}`}>
                                {s.title}
                            </span>
                            {i < steps.length - 1 && (
                                <div className={`w-8 h-0.5 ${i < step ? 'bg-brand-red' : 'bg-line-default'}`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Step 0: Company Info */}
                {step === 0 && (
                    <div className="space-y-4" data-testid="onboarding-step-1">
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Company Name *</Label>
                            <Input value={form.company_name} onChange={e => update('company_name', e.target.value)} className="input-base" placeholder="Acme Corp" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Website</Label>
                            <Input value={form.website} onChange={e => update('website', e.target.value)} className="input-base" placeholder="https://acme.com" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Company Summary</Label>
                            <Textarea value={form.company_summary} onChange={e => update('company_summary', e.target.value)} className="input-base min-h-[80px]" placeholder="Brief description of your company..." />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Business Objectives</Label>
                            <Textarea value={form.business_objectives} onChange={e => update('business_objectives', e.target.value)} className="input-base min-h-[80px]" placeholder="What are you aiming to achieve with ABM?" />
                        </div>
                    </div>
                )}

                {/* Step 1: Solutions */}
                {step === 1 && (
                    <div className="space-y-4" data-testid="onboarding-step-2">
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Solutions / Products (one per line)</Label>
                            <Textarea value={form.solutions} onChange={e => update('solutions', e.target.value)} className="input-base min-h-[100px]" placeholder="Enterprise Cloud Platform&#10;Data Analytics Suite" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Value Propositions (one per line)</Label>
                            <Textarea value={form.value_propositions} onChange={e => update('value_propositions', e.target.value)} className="input-base min-h-[100px]" placeholder="50% faster deployment&#10;99.9% uptime SLA" />
                        </div>
                    </div>
                )}

                {/* Step 2: Targeting */}
                {step === 2 && (
                    <div className="space-y-4" data-testid="onboarding-step-3">
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Target Industries (comma separated)</Label>
                            <Input value={form.target_industries} onChange={e => update('target_industries', e.target.value)} className="input-base" placeholder="Financial Services, Healthcare, Manufacturing" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Target Company Sizes (comma separated)</Label>
                            <Input value={form.target_company_sizes} onChange={e => update('target_company_sizes', e.target.value)} className="input-base" placeholder="1000-5000, 5000+" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Target Geography (comma separated)</Label>
                            <Input value={form.target_geography} onChange={e => update('target_geography', e.target.value)} className="input-base" placeholder="United States, Canada, UK" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-content-secondary">Ideal Customer Profile (ICP) Summary</Label>
                            <Textarea value={form.icp_summary} onChange={e => update('icp_summary', e.target.value)} className="input-base min-h-[80px]" placeholder="Enterprise companies (1000+ employees) in financial services..." />
                        </div>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-4 border-t border-line-default">
                    {step > 0 ? (
                        <Button variant="ghost" className="btn-ghost gap-2" onClick={() => setStep(s => s - 1)}>
                            <ArrowLeft className="w-4 h-4" /> Back
                        </Button>
                    ) : (
                        <Button variant="ghost" className="btn-ghost" onClick={logout}>Sign Out</Button>
                    )}
                    {step < steps.length - 1 ? (
                        <Button className="btn-primary gap-2" onClick={() => setStep(s => s + 1)} data-testid="onboarding-next-btn">
                            Next <ArrowRight className="w-4 h-4" />
                        </Button>
                    ) : (
                        <Button className="btn-primary gap-2" onClick={handleSubmit} disabled={saving} data-testid="onboarding-submit-btn">
                            {saving ? 'Submitting...' : 'Submit for Approval'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
